import { DataTheme } from 'src/Enums'
import { createIntl, createIntlCache, IntlShape } from 'react-intl'
import messages_da from '../translations/da.json'
import { LayerGroup } from 'src/dataSources/layers/LayerRegistry'

export interface BarStyle {
  transition: string
}

export interface BarTransitionStyleState {
  width?: string
  height?: string
}

export interface BarTransitionStyle {
  entering: BarTransitionStyleState
  entered: BarTransitionStyleState
  exiting: BarTransitionStyleState
  exited: BarTransitionStyleState
  unmounted: BarTransitionStyleState
}

/**
 * Get IntlShape object that can be used for getting localized texts.
 * @returns IntlShape object.
 */
export const getIntl = (): IntlShape => {
  const cache = createIntlCache()
  return createIntl({
    locale: 'da',
    messages: messages_da
  }, cache)
}

/**
 * Get localized data theme name.
 * @param type DateTheme type.
 * @returns Localized data theme name.
 */
export const getDataThemeName = (type: DataTheme): string => {
  const intl = getIntl()
  switch (type) {
    case DataTheme.NONE:
      return intl.formatMessage({ id: 'theme.live' })
    case DataTheme.ICE_AGE:
      return intl.formatMessage({ id: 'theme.iceage' })
    case DataTheme.CLIMATE:
      return intl.formatMessage({ id: 'theme.climate' })
    case DataTheme.ENERGY:
      return intl.formatMessage({ id: 'theme.energy' })
    case DataTheme.ENVIRONMENT:
      return intl.formatMessage({ id: 'theme.environment' })
  }
}

/**
 * Get localized layer group name.
 * @param group Group to get name for.
 * @returns Localized layer group name.
 */
export const getLayerGroupName = (group: LayerGroup): string => {
  const intl = getIntl()
  switch (group) {
    case LayerGroup.GENERAL:
      return intl.formatMessage({ id: 'map.layergroup.general' })
    case LayerGroup.NATURE:
      return intl.formatMessage({ id: 'map.layergroup.nature' })
    case LayerGroup.SUBSURFACE:
      return intl.formatMessage({ id: 'map.layergroup.subsurface' })
    case LayerGroup.WATER:
      return intl.formatMessage({ id: 'map.layergroup.water' })
    case LayerGroup.WINDPOWER:
      return intl.formatMessage({ id: 'map.layergroup.windpower' })
    case LayerGroup.POLLUTION:
      return intl.formatMessage({ id: 'map.layergroup.pollution' })
    case LayerGroup.GEOPHYSICS:
      return intl.formatMessage({ id: 'map.layergroup.geophysics' })
    case LayerGroup.ICE_AGE:
      return intl.formatMessage({ id: 'map.layergroup.iceage' })
    case LayerGroup.NEAR_TERRAIN:
      return intl.formatMessage({ id: 'map.layergroup.nearterrain' })
    case LayerGroup.BACKGROUND_MAPS:
      return intl.formatMessage({ id: 'map.layergroup.backgroundmaps' })
    case LayerGroup.POINTS_OF_INTEREST:
      return intl.formatMessage({ id: 'map.layergroup.pointsofinterest' })
    }
}

/**
 * Get style for bar.
 * @param speed Speed to transition with.
 * @returns Bar style object.
 */
export const getBarStyle = (speed: number): BarStyle => {
  return {
    transition: `width ${speed}ms`
  }
}

/**
 * Get bar transition style.
 * @param width Width of sidebar in pixels.
 * @returns Bar transition style object.
 */
export const getBarTransitionStyle = (width: number): BarTransitionStyle => {
  return {
    entering: { width: '0px' },
    entered: { width: `${width}px` },
    exiting: { width: `${width}px` },
    exited: { width: '0px' },
    unmounted: { width: '0px' }
  }
}

/**
 * Converts a number into a hex string
 * @param c Number to be converted
 * @returns the number converted into a hex string
 */
export const numberToHex = (c: number): string => {
  const hex = c.toString(16).toUpperCase()
  return hex.length === 1 ? `0${hex}` : hex
}

/**
 * Converts a rgb color into a hex color
 * @param r the red channel
 * @param g the green channel
 * @param b the blue channel
 * @returns returns the color converted into a hex string
 */
export const rgbToHex = (r: number, g: number, b: number): string => {
  return `#${numberToHex(r)}${numberToHex(g)}${numberToHex(b)}`
}

/**
 * Rounds a given number to the desired number of decimal places and retuns it as a string
 * @param num The number to turn into a string
 * @param decimalPlaces the amount of decimal places to round the number to
 * @returns the rounded number as a string
 */
export const numberToDecimalString = (num: number, decimalPlaces: number = 2): string => {
  return (Math.round(num * 100) / 100).toFixed(decimalPlaces)
}

/**
 * Replaces special characters like æ, ø and å
 * @param str string for which to raplace chars
 * @returns string with replaced chars
 */
export const replaceSpecialChars = (str: string): string => {
  return str
    .replaceAll('æ', 'ae')
    .replaceAll('ø', 'oe')
    .replaceAll('å', 'aa')
}
